/**
 * DimensionManager - Handles displaying furniture dimensions using 3D objects
 * Optimized version with:
 * - persistent meshes
 * - cached canvas textures
 * - correct enable / disable behavior
 * - proper 2D / 3D text flipping
 */

import * as THREE from 'three';
import { fetchApiJson } from './ApiData.js';
import { getWorldYRotation, orientedXZGap } from '../utils/geometry.js';

// Upper bound on how long the input gate may skip measuring (~0.5 s at 60 fps).
const REMEASURE_FRAMES = 30;

// Constants
const DIMENSION_COLOR = 0x0080ff;
const FLOOR_HEIGHT = 0.01;
// A leg-named mesh only counts as a leg if it sits in this bottom fraction of its module — keeps top wood surfaces (e.g. oiko-ts) out of the leg-height math.
const LEG_REGION_FRACTION = 0.5;
const LEG_PATTERNS = ['leg', '_wood', 'frame', 'wood'];
const NOOP = () => {};
const TEXT_SIZE = 0.15;
// Distance (m) from the footprint edge to the arrow line / label centre; the label plane is
// TEXT_SIZE * 1.1 tall, so its inner edge lands just outside the line.
const LINE_OFFSET = 0.07;
const LABEL_OFFSET = 0.16;
const HEIGHT_LINE_OFFSET = 0.02;
const CM_TO_INCHES = 0.393701;
// Yaws are folded into (-45°, 45°] before being compared — a rectangle measures the same after a
// quarter turn, so an L-shape's 0°/90° modules share one measuring frame.
const QUARTER_TURN = Math.PI / 2;
// Yaws within ~0.6° count as the same frame; below it a group is treated as world-aligned.
const YAW_TOLERANCE = 0.01;
// Pieces whose plan footprints come within this gap (m) are measured as one group.
const GROUP_RADIUS = 0.02;
// A step in the footprint profile smaller than this (m) isn't an L — a cushion overhanging its
// module by a few cm must not spawn its own dimension line.
const PROFILE_STEP_TOLERANCE = 0.12;
// Footprint runs shorter than this (m) merge into a neighbour; under a bard R's 25 cm (mesh 24.98), the shallowest arm.
const MIN_PROFILE_RUN = 0.2;
// Past this the outline isn't a shape anyone reads off a drawing; fall back to the plain box.
const MAX_OUTLINE_SEGMENTS = 10;
// Label spin per side as [2D, 3D], so the number reads right-way-up from the active camera.
const LABEL_ROTATION = {
  'x-': [0, Math.PI],
  'x+': [Math.PI, 0],
  'z-': [QUARTER_TURN, 3 * QUARTER_TURN],
  'z+': [3 * QUARTER_TURN, QUARTER_TURN],
};
const NO_COLLECTION_HEIGHT = { height: null, hasSeatHeights: false };
const EMPTY_MAP = new Map();

export class DimensionManager {
  constructor(app) {
    this.app = app;
    this.enabled = true;
    // Forced off by a renderer that can't draw the dimension meshes (path tracing),
    // kept separate from `enabled` so the user's own choice survives and persists.
    this.suppressed = false;
    this.unit = 'cm';

    this.dimensionGroup = new THREE.Group();
    this.dimensionGroup.name = 'DimensionGroup';

    // One reused slot of meshes (4 lines + 4 labels + height line/labels) per measured group, grown on demand.
    this.slots = [];
    this.textCache = new Map();
    this.lastStateKey = '';
    // Skip-the-measure gate (see _inputsUnchanged): last frame's input signature, a scratch
    // one for this frame, an explicit dirty flag for edits that don't move a root, and a
    // frame counter that forces a periodic re-measure in case some edit path never says so.
    this._inputSig = [];
    this._inputSigScratch = [];
    this._dirty = true;
    this._framesSinceMeasure = 0;

    this._collectionHeightCache = new Map();

    // Scratch for the per-frame measuring pass.
    this._groupMembers = new Set();
    this._groupSeen = new Set();
    this._planBoxes = new Map();
    this._planFrame = new THREE.Matrix4();
    this._sceneBody = new Map();
    // Mesh boxes are wanted twice per module per pass (footprint + body); cleared per group.
    this._boxCache = new Map();
    // Lowercased `footprintExcludeNodes`, keyed by the catalog array itself — update() runs per
    // frame, so the normalisation must not re-allocate per element per pass.
    this._excludeCache = new WeakMap();
    this._scratchMatrix = new THREE.Matrix4();
    this._frameMatrix = new THREE.Matrix4();
    this._scratchVec = new THREE.Vector3();

    // Product dimensions from API
    this.productDimensions = null;

    this.init();
    this.loadProductDimensions();
  }

  /**
   * Load product dimensions from API
   */
  async loadProductDimensions() {
    const data = await fetchApiJson('product-dimensions', { snapshot: 'product-dimensions.json' });
    if (!data) {
      console.warn('No product dimensions data available');
      this.productDimensions = null;
      return;
    }
    this.productDimensions = data;
    this._collectionHeightCache.clear();
    this.lastStateKey = '';
  }

  init() {
    const scene = this.app.getScene();
    if (scene) scene.add(this.dimensionGroup);
  }

  /* =====================
     Public API
     ===================== */

  setEnabled(enabled) {
    if (this.enabled === enabled) return;

    this.enabled = enabled;
    this.lastStateKey = '';

    enabled ? this.update() : this.clear();
  }

  isEnabled() {
    return this.enabled;
  }

  setSuppressed(suppressed) {
    if (this.suppressed === suppressed) return;

    this.suppressed = suppressed;
    this.lastStateKey = '';

    suppressed ? this.clear() : this.update();
  }

  isSuppressed() {
    return this.suppressed;
  }

  setUnit(unit) {
    if (this.unit === unit) return;

    this.unit = unit;
    this.lastStateKey = '';

    if (this.enabled) this.update();
  }

  getUnit() {
    return this.unit;
  }

  /* =====================
     Internal helpers
     ===================== */

  is3DMode() {
    const cameraManager = this.app.getCameraManager();
    return cameraManager && cameraManager.sceneMode === '3D';
  }

  isWhiteRoom() {
    return this.app.getSceneManager()?.getEnvironmentManager()?.isWhiteRoom() === true;
  }

  convertToUnit(valueInMeters) {
    const cm = valueInMeters * 100;
    if (this.unit === 'inches') {
      return (cm * CM_TO_INCHES).toFixed(1);
    }
    const rounded = Math.round(cm * 10) / 10;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  }

  getUnitLabel() {
    return this.unit === 'inches' ? 'in' : 'cm';
  }

  // Footprint lengths print as whole cm (tenths of an inch); the height label uses convertToUnit.
  formatLength(metres) {
    const cm = metres * 100;
    return `${this.unit === 'inches' ? (cm * CM_TO_INCHES).toFixed(1) : Math.round(cm)} ${this.getUnitLabel()}`;
  }

  clear() {
    this.slots.forEach(slot => this.hideSlot(slot));
    this._boxCache.clear();
  }

  hideSlot(slot) {
    slot.texts.forEach(m => (m.visible = false));
    slot.lines.forEach(g => (g.visible = false));
    slot.heightLine.visible = false;
    slot.heightText.visible = false;
    slot.heightTextBack.visible = false;
  }

  /* =====================
     Update logic
     ===================== */

  /** A committed edit may have changed geometry without moving a root (covers, attachments). */
  invalidate() {
    this._dirty = true;
  }

  /**
   * Cheap stand-in for re-measuring: true when nothing the measurement reads has changed
   * since last frame — every piece's local transform, parent, variant and visibility, plus the
   * view settings. A piece mid-tween always re-measures, since its child nodes are moving.
   */
  _inputsUnchanged() {
    const all = this.app.getFurnitureManager?.()?.getAllFurniture?.() || [];
    const sig = this._inputSigScratch;
    sig.length = 0;
    sig.push(this.unit, this.is3DMode(), this.isWhiteRoom(),
      this.app.getFurnitureManager?.()?.currentLegHeight, all.length);
    for (const f of all) {
      if (f.isAnimating?.()) return false;
      const mesh = f.getMesh?.();
      if (!mesh) { sig.push(null); continue; }
      const { position: p, quaternion: q } = mesh;
      sig.push(mesh.id, mesh.parent?.id, mesh.visible, f.variantIndex, p.x, p.y, p.z, q.x, q.y, q.z, q.w);
    }

    const prev = this._inputSig;
    let same = prev.length === sig.length;
    for (let i = 0; same && i < sig.length; i++) same = prev[i] === sig[i];
    this._inputSig = sig;
    this._inputSigScratch = prev;
    return same;
  }

  // Groups by links (over all pieces, so an excluded one can't split a chain) or GROUP_RADIUS proximity (measurable only).
  buildMeasurementGroups(furniture, measurable) {
    const members = this._groupMembers;
    const seen = this._groupSeen;
    const planBoxes = this._planBoxes;
    members.clear();
    seen.clear();
    planBoxes.clear();
    for (const item of furniture) members.add(item);
    for (const item of furniture) {
      if (!measurable(item)) continue;
      const box = this.elementPlanBox(item);
      if (box) planBoxes.set(item, box);
    }
    const groups = [];

    const visit = (other, stack) => {
      if (members.has(other) && !seen.has(other)) {
        seen.add(other);
        stack.push(other);
      }
    };

    for (const item of furniture) {
      if (seen.has(item)) continue;
      const group = [];
      const stack = [item];
      seen.add(item);
      while (stack.length > 0) {
        const current = stack.pop();
        group.push(current);
        const parent = current.attachedTo?.parentFurniture;
        if (parent) visit(parent, stack);
        current.attachedChildren?.forEach(child => visit(child, stack));
        const box = planBoxes.get(current);
        if (!box) continue;
        for (const [other, otherBox] of planBoxes) {
          if (!seen.has(other) && orientedXZGap(box, otherBox) <= GROUP_RADIUS) visit(other, stack);
        }
      }
      groups.push(group);
    }
    planBoxes.clear();
    return groups;
  }

  // Plan-view oriented box in the element's own yaw (a 45° piece isn't padded to its AABB); null without geometry.
  elementPlanBox(element) {
    const mesh = element.getMesh?.();
    if (!mesh) return null;
    const yaw = getWorldYRotation(mesh);
    const box = this.getElementWorldBox(element, this._planFrame.makeRotationY(-yaw), false);
    if (!box || box.isEmpty()) return null;
    const center = this.frameToWorld((box.min.x + box.max.x) / 2, (box.min.z + box.max.z) / 2, yaw);
    return {
      cx: center.x,
      cz: center.z,
      cos: Math.cos(yaw),
      sin: Math.sin(yaw),
      hx: (box.max.x - box.min.x) / 2,
      hz: (box.max.z - box.min.z) / 2,
    };
  }

  // One group per cluster of pieces that sit together; zip shelving is dropped after grouping.
  collectMeasurementGroups() {
    const all = this.app.getFurnitureManager?.()?.getAllFurniture?.() || [];
    const isMeasurable = f => f.furnitureData?.collection !== 'zip';

    return this.buildMeasurementGroups(all, isMeasurable)
      .map(group => group.filter(isMeasurable))
      .filter(group => group.length > 0);
  }

  /** Fold a yaw into (-45°, 45°] — the frame a rectangle measures the same in. */
  toFrameYaw(yaw) {
    let a = yaw % QUARTER_TURN;
    if (a > QUARTER_TURN / 2) a -= QUARTER_TURN;
    else if (a <= -QUARTER_TURN / 2) a += QUARTER_TURN;
    return a;
  }

  /** Angular distance between two frame yaws, accounting for the wrap at ±45°. */
  frameYawDelta(a, b) {
    const d = Math.abs(a - b) % QUARTER_TURN;
    return Math.min(d, QUARTER_TURN - d);
  }

  // Frame → world: a +yaw turn about Y. Returns a scratch vector (y left at 0) — read it before
  // the next call.
  frameToWorld(x, z, yaw) {
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    return this._scratchVec.set(x * cos + z * sin, 0, -x * sin + z * cos);
  }

  // Yaw this group is measured in, so a diagonal sofa gets its true width/depth and diagonal
  // dimension lines instead of an oversized world-aligned box. It's the yaw most of the group's
  // modules agree on; with no majority — free-form pieces at mixed angles when snapping is off —
  // the group stays world-aligned at 0.
  computeFrameYaw(furniture) {
    const modules = furniture.filter(f => f.furnitureData?.componentType === 'module');
    const source = modules.length > 0 ? modules : furniture;
    const buckets = [];
    let counted = 0;

    for (const item of source) {
      const mesh = item.getMesh?.();
      if (!mesh) continue;
      const yaw = this.toFrameYaw(getWorldYRotation(mesh));
      counted++;
      const bucket = buckets.find(b => this.frameYawDelta(b.yaw, yaw) < YAW_TOLERANCE);
      if (bucket) bucket.count++;
      else buckets.push({ yaw, count: 1 });
    }

    if (counted === 0) return 0;
    let best = buckets[0];
    for (const b of buckets) if (b.count > best.count) best = b;
    if (best.count * 2 <= counted) return 0;
    return Math.abs(best.yaw) < YAW_TOLERANCE ? 0 : best.yaw;
  }

  /** Everything needed to draw one group's dimensions, or null when it has no measurable geometry. */
  measureGroup(furniture) {
    // Boxes are built in the group's own frame (world → frame is a -yaw turn about Y) and the
    // renderer turns the finished layout back by +yaw. The frame matrix is scratch, live only for
    // this call — every box below is baked out before it returns.
    const yaw = this.computeFrameYaw(furniture);
    const frame = yaw === 0 ? null : this._frameMatrix.makeRotationY(-yaw);
    this._boxCache.clear();

    // Body box of the height-defining modules (legs detected separately), for the HEIGHT dimension.
    const sources = this.heightSourceModules(furniture);
    const seaterResult = this.calculateSeaterBodyBoundingBox(sources, frame);
    const seaterBbox = seaterResult ? seaterResult.body : null;
    const bodyByKey = seaterResult ? seaterResult.bodyByKey : EMPTY_MAP;

    // Footprint (width/depth) is the TRUE bounding box of the actual meshes — exact by
    // construction, panels included automatically. It replaces an older per-module catalog-
    // rectangle reconstruction that drifted a few cm off (some GLTF pivots sit 5-9 cm off-centre).
    // Falls back to the body box, then the overall box, when no structural meshes are present.
    const footprintBoxes = this.collectFootprintBoxes(furniture, frame);
    const bbox = this.unionBoxes(footprintBoxes) || seaterBbox || this.calculateOverallBoundingBox(furniture, frame);
    if (!bbox || bbox.isEmpty()) return null;

    // World-space centre, for a slot ordering that doesn't jump when a group rotates.
    const center = this.frameToWorld((bbox.min.x + bbox.max.x) / 2, (bbox.min.z + bbox.max.z) / 2, yaw);

    return {
      yaw, bbox, seaterBbox, bodyByKey,
      segments: this.buildOutlineSegments(footprintBoxes, bbox),
      centerX: center.x,
      centerZ: center.z,
      // Legs are only added back on for a collection that has adjustable ones (bonbon3, zip don't).
      hasLegs: this.groupHasLegs(sources.modules),
      // Catalog height drives the vertical dimension where it exists; geometry is the fallback.
      apiHeight: this.getApiBodyHeight(furniture),
    };
  }

  // Body height (cm, legs excluded) per group. Catalog height wins; the geometry fallback is
  // resolved scene-wide rather than per group, so two groups of the same thing can't print
  // different numbers — SKUs of one line aren't modelled to identical heights (diagon-lol is
  // 0.8 cm shorter than diagon-stl) while a catalog height is one number per line.
  resolveBodyHeights(measurements) {
    const sceneBody = this._sceneBody;
    sceneBody.clear();
    for (const m of measurements) {
      for (const [key, height] of m.bodyByKey) {
        const known = sceneBody.get(key);
        if (known === undefined || height > known) sceneBody.set(key, height);
      }
    }

    for (const m of measurements) {
      if (m.apiHeight != null) {
        m.bodyHeightCm = m.apiHeight;
        continue;
      }
      let tallest = null;
      for (const key of m.bodyByKey.keys()) {
        const height = sceneBody.get(key);
        if (height != null && (tallest === null || height > tallest)) tallest = height;
      }
      m.bodyHeightCm = tallest === null ? null : tallest * 100;
    }
  }

  update() {
    if (!this.enabled || this.suppressed) {
      this.clear();
      return;
    }

    const drawn = this.lastStateKey !== '' && this.slots.length > 0 && this.slots[0].texts[0]?.visible;
    if (drawn && !this._dirty && this._framesSinceMeasure < REMEASURE_FRAMES && this._inputsUnchanged()) {
      this._framesSinceMeasure++;
      return;
    }
    this._dirty = false;
    this._framesSinceMeasure = 0;

    const measurements = this.collectMeasurementGroups()
      .map(group => this.measureGroup(group))
      .filter(Boolean)
      // Stable slot assignment across updates, so a group keeps its meshes as others move.
      .sort((a, b) => (a.centerX - b.centerX) || (a.centerZ - b.centerZ));

    if (measurements.length === 0) {
      this.clear();
      return;
    }

    this.resolveBodyHeights(measurements);

    const mode = this.is3DMode() ? '3D' : '2D';
    const legHeight = this.app.getFurnitureManager?.()?.currentLegHeight || '8';
    const envKey = this.isWhiteRoom() ? 'white' : 'default';
    // Only what the renderer actually draws: the outline segments, the corner the height line
    // hangs off, the frame, the printed height and whether there IS one (the body box's own
    // extents never reach the screen).
    let key = `${this.unit}-${mode}-${legHeight}-${envKey}`;
    for (const m of measurements) {
      key += `|${m.bbox.min.x},${m.bbox.max.z},${m.yaw},${m.bodyHeightCm},${m.seaterBbox ? 1 : 0},${m.hasLegs ? 1 : 0}`;
      for (const s of m.segments) key += `;${s.axis}${s.sign},${s.start},${s.end},${s.edge}`;
    }

    // If state hasn't changed and meshes are visible, skip
    if (
      key === this.lastStateKey &&
      this.slots.length > 0 &&
      this.slots[0].texts[0]?.visible
    ) {
      return;
    }

    this.lastStateKey = key;

    measurements.forEach((m, i) => {
      this.render3DDimensions(this.getSlot(i), m);
    });
    for (let i = measurements.length; i < this.slots.length; i++) {
      this.hideSlot(this.slots[i]);
    }
  }

  calculateOverallBoundingBox(furniture, frame = null) {
    const box = new THREE.Box3();
    furniture.forEach(item => {
      const b = this.getElementWorldBox(item, frame);
      if (b) box.union(b);
    });
    return box;
  }

  // A node counts only if it and every ancestor up to the element root are visible. Variant data
  // can set visible:false to hide geometry, which must not inflate dimensions. A node fading OUT
  // stays visible:true for the whole transition, so we honour its target visibility and drop it.
  isNodeVisible(node, root) {
    let n = node;
    while (n) {
      if (n.visible === false) return false;
      if (n.userData?._fadeActive && n.userData._fadeTargetVisible === false) return false;
      if (n === root) break;
      n = n.parent;
    }
    return true;
  }

  // Place any fading-IN node at its settled (rest) pose so we measure the target state, not the
  // transient 0.3m slide. Returns a restore fn; caller does the single updateMatrixWorld.
  freezeFadingNodesAtRest(mesh) {
    let changed = null;
    mesh.traverse(n => {
      if (n.userData?._fadeActive && n.userData._fadeTargetVisible === true && n.userData._fadeRestPos) {
        (changed ??= []).push({ node: n, pos: n.position.clone(), quat: n.quaternion.clone() });
        n.position.copy(n.userData._fadeRestPos);
        if (n.userData._fadeRestQuat) n.quaternion.copy(n.userData._fadeRestQuat);
      }
    });
    if (!changed) return NOOP;
    return () => {
      for (const c of changed) {
        c.node.position.copy(c.pos);
        c.node.quaternion.copy(c.quat);
      }
      mesh.updateMatrixWorld(true);
    };
  }

  // Meshes belonging to attached children — separate furniture entries measured on their own pass.
  collectAttachedMeshes(element) {
    const set = new Set();
    element.attachedChildren?.forEach(child => {
      const childMesh = child.getMesh?.();
      if (childMesh) childMesh.traverse(m => set.add(m));
    });
    return set;
  }

  // [{name, box}] of own visible meshes (fading-in at rest), in world or `frame`; cached per group pass unless `cache: false`.
  collectElementMeshBoxes(element, frame = null, cache = true) {
    const cached = cache && this._boxCache.get(element);
    if (cached) return cached;

    const mesh = element.getMesh?.();
    if (!mesh) return [];
    const attachedMeshes = this.collectAttachedMeshes(element);
    // Only fading nodes need the rest-pose freeze; skip the extra traverse when nothing animates.
    const restore = element.isAnimating?.() ? this.freezeFadingNodesAtRest(mesh) : NOOP;
    mesh.updateMatrixWorld(true);
    const boxes = [];
    mesh.traverse(child => {
      if (child.isMesh && !attachedMeshes.has(child) && child.geometry && this.isNodeVisible(child, mesh)) {
        // Geometry is static (GLTF); only the world matrix changes, so compute the local box once.
        if (!child.geometry.boundingBox) child.geometry.computeBoundingBox();
        if (child.geometry.boundingBox) {
          const matrix = frame ? this._scratchMatrix.multiplyMatrices(frame, child.matrixWorld) : child.matrixWorld;
          boxes.push({ name: (child.name || '').toLowerCase(), box: child.geometry.boundingBox.clone().applyMatrix4(matrix) });
        }
      }
    });
    restore();
    if (cache) this._boxCache.set(element, boxes);
    return boxes;
  }

  /** Bounding box of one element's own meshes (attached children excluded). Box3 or null. */
  getElementWorldBox(element, frame = null, cache = true) {
    const boxes = this.collectElementMeshBoxes(element, frame, cache);
    if (boxes.length === 0) return null;
    const box = new THREE.Box3();
    for (const b of boxes) box.union(b.box);
    return box;
  }

  // Lowercased exclude patterns for one catalog array, cached on the array itself.
  normalizedExcludes(patterns) {
    let low = this._excludeCache.get(patterns);
    if (!low) {
      low = patterns.map(p => String(p).toLowerCase());
      this._excludeCache.set(patterns, low);
    }
    return low;
  }

  /**
   * One element's footprint box, with any node named in `properties.footprintExcludeNodes`
   * dropped. Box3 or null.
   *
   * Most lines are measured to the outermost cushion, which is what the plain mesh box gives.
   * A line whose catalog overall size is measured to the frame instead needs the loose parts
   * dropped or it reads several cm over — clooods models its pillows and back supports up to
   * 2.7 cm proud of the base, so the 91x91 corner measured 96x95. Patterns match as substrings
   * of the lowercased node name (like LEG_PATTERNS), so one `pillow` entry covers `pillow_1`
   * and `armchair_pillow`.
   */
  getElementFootprintBox(element, frame = null) {
    const patterns = element.furnitureData?.properties?.footprintExcludeNodes;
    if (!patterns?.length) return this.getElementWorldBox(element, frame);

    const low = this.normalizedExcludes(patterns);
    const boxes = this.collectElementMeshBoxes(element, frame);
    const box = new THREE.Box3();
    let kept = false;
    for (const b of boxes) {
      if (low.some(p => b.name.includes(p))) continue;
      box.union(b.box);
      kept = true;
    }
    // A list that happens to match every node (renamed geometry) must not silently zero the
    // footprint — measuring the whole piece is the safer miss.
    return kept ? box : this.getElementWorldBox(element, frame);
  }

  // True footprint = the boxes of the WHOLE assembled piece, exactly as a tape measure around it
  // would read: modules, panels/backrests, and the cushions/accessories that stick out (the
  // catalog overall dimension is measured to the outermost cushion, not the bare frame). Kept as
  // a list, not a union, so the L-shape outline can be read off the individual pieces.
  // Zip shelving is already filtered out by the caller.
  collectFootprintBoxes(furniture, frame = null) {
    const boxes = [];
    furniture.forEach(item => {
      // Cascais side/connector panels (cascais-d) are decorative trim that sits flush with the
      // module body in the catalog spec. At runtime the attachment system seats them with a small
      // outward gap, which inflates the measured footprint by a few cm (e.g. cascais-ra reads
      // 175x201 instead of the catalog 173x198). Exclude them so the printed dimension matches
      // the catalog overall size. Other collections' panels (e.g. matacao3's h75/h95 back panels)
      // are structural and legitimately define the depth, so they stay in.
      if (
        item.furnitureData?.componentType === 'panel' &&
        item.furnitureData?.collection === 'cascais'
      ) return;
      const b = this.getElementFootprintBox(item, frame);
      if (b) boxes.push(b);
    });
    return boxes;
  }

  /** Union of `collectFootprintBoxes`. Box3 or null. */
  calculateFootprintBoundingBox(furniture, frame = null) {
    return this.unionBoxes(this.collectFootprintBoxes(furniture, frame));
  }

  unionBoxes(boxes) {
    if (boxes.length === 0) return null;
    const box = new THREE.Box3();
    for (const b of boxes) box.union(b);
    return box;
  }

  /* =====================
     Footprint outline
     ===================== */

  /**
   * Slice the footprint into runs along `axis` ('x' or 'z'): contiguous slabs over which the
   * extent on the other axis (`cross`) is constant. A rectangle gives one run; an L gives two —
   * the deep arm and the shallow one. Null when the footprint has a gap along the axis (detached
   * pieces measured as one group), where an outline is meaningless and the box is the honest read.
   */
  profileRuns(boxes, axis, cross) {
    const cuts = [...new Set(boxes.flatMap(b => [b.min[axis], b.max[axis]]))].sort((a, b) => a - b);
    const runs = [];

    for (let i = 0; i < cuts.length - 1; i++) {
      const start = cuts[i];
      const end = cuts[i + 1];
      if (end - start < 1e-4) continue;
      const mid = (start + end) / 2;
      let lo = Infinity;
      let hi = -Infinity;
      for (const b of boxes) {
        if (b.min[axis] > mid || b.max[axis] < mid) continue;
        lo = Math.min(lo, b.min[cross]);
        hi = Math.max(hi, b.max[cross]);
      }
      if (lo === Infinity) {
        // A hairline gap (a seat mesh 0.2 mm short of its armrest) isn't a footprint gap; the previous run spans it.
        if (end - start > GROUP_RADIUS || runs.length === 0) return null;
        runs[runs.length - 1].end = end;
        continue;
      }
      runs.push({ start, end, lo, hi });
    }

    return runs.length > 0 ? this.mergeProfileRuns(runs) : null;
  }

  // Collapse neighbours that describe the same slab. Runs to exhaustion BEFORE slivers are
  // considered: where two pieces overlap slightly the overlap is its own thin slab, and it
  // belongs to whichever neighbour it matches, not to whichever rule happens to fire first.
  mergeAlignedRuns(runs) {
    for (let i = 0; i < runs.length - 1; ) {
      const a = runs[i];
      const b = runs[i + 1];
      if (this.profileDistance(a, b) >= PROFILE_STEP_TOLERANCE) { i++; continue; }
      runs.splice(i, 2, { start: a.start, end: b.end, lo: Math.min(a.lo, b.lo), hi: Math.max(a.hi, b.hi) });
      // Step back — the merged run may now also fold into the one before it.
      if (i > 0) i--;
    }
    return runs;
  }

  /** How far apart two runs' cross-profiles are; below the step tolerance they're one slab. */
  profileDistance(a, b) {
    return Math.max(Math.abs(a.lo - b.lo), Math.abs(a.hi - b.hi));
  }

  // What's left that's too short to carry a dimension of its own is a bump on the side of the
  // piece. It hands its span to the adjacent run whose profile it matches best — not simply the
  // longer one — and keeps that run's profile, so the bump's overhang can't spread across its
  // host and read as a step. How far it sticks out is already in the overall box.
  absorbSliverRuns(runs) {
    while (runs.length > 1) {
      let idx = -1;
      let shortest = MIN_PROFILE_RUN;
      for (let i = 0; i < runs.length; i++) {
        const length = runs[i].end - runs[i].start;
        if (length < shortest) { shortest = length; idx = i; }
      }
      if (idx === -1) break;

      const sliver = runs[idx];
      const prev = runs[idx - 1];
      const next = runs[idx + 1];
      const host = !prev || (next && this.profileDistance(sliver, next) < this.profileDistance(sliver, prev)) ? next : prev;
      host.start = Math.min(host.start, sliver.start);
      host.end = Math.max(host.end, sliver.end);
      runs.splice(idx, 1);
    }
    return runs;
  }

  mergeProfileRuns(runs) {
    // Absorbing a sliver can leave its host flush with the run beyond it, so align again after.
    return this.mergeAlignedRuns(this.absorbSliverRuns(this.mergeAlignedRuns(runs)));
  }

  /** Merged runs back as boxes, so the second axis can be profiled off the simplified shape. */
  runBoxes(runs, axis, cross) {
    return runs.map(r => ({
      min: { [axis]: r.start, [cross]: r.lo },
      max: { [axis]: r.end, [cross]: r.hi },
    }));
  }

  // Consecutive runs flush on the `edge` side ('lo' or 'hi') share one dimension line, so the
  // unstepped side of an L still prints a single overall span.
  groupRunEdges(runs, edge) {
    const groups = [];
    for (const run of runs) {
      const last = groups[groups.length - 1];
      if (last && Math.abs(last.edge - run[edge]) < PROFILE_STEP_TOLERANCE) {
        last.end = run.end;
        last.edge = edge === 'lo' ? Math.min(last.edge, run.lo) : Math.max(last.edge, run.hi);
      } else {
        groups.push({ start: run.start, end: run.end, edge: run[edge] });
      }
    }
    return groups;
  }

  /**
   * The dimension lines for one group, as `{ axis, start, end, edge, sign }` in frame space:
   * `axis` is the direction measured, `edge`/`sign` the cross coordinate of the footprint side the
   * line hangs off and which way is out. A rectangle yields the same four it always did; an L
   * yields six — the two overall spans on its unstepped sides, plus each arm's own run and depth.
   */
  buildOutlineSegments(boxes, bbox) {
    const xRuns = boxes.length > 0 ? this.profileRuns(boxes, 'x', 'z') : null;
    if (!xRuns) return this.rectangleSegments(bbox);
    // The second pass reads the simplified x rectangles rather than the raw meshes, so both axes
    // agree on which bumps are arms — a nub absorbed along x can't come back as a step along z.
    const zRuns = this.profileRuns(this.runBoxes(xRuns, 'x', 'z'), 'z', 'x');
    if (!zRuns) return this.rectangleSegments(bbox);

    const segments = [];
    for (const [runs, axis] of [[xRuns, 'x'], [zRuns, 'z']]) {
      for (const [edge, sign] of [['lo', -1], ['hi', 1]]) {
        for (const g of this.groupRunEdges(runs, edge)) {
          segments.push({ axis, start: g.start, end: g.end, edge: g.edge, sign });
        }
      }
    }
    return segments.length > MAX_OUTLINE_SEGMENTS ? this.rectangleSegments(bbox) : segments;
  }

  rectangleSegments({ min, max }) {
    return [
      { axis: 'x', start: min.x, end: max.x, edge: min.z, sign: -1 },
      { axis: 'x', start: min.x, end: max.x, edge: max.z, sign: 1 },
      { axis: 'z', start: min.z, end: max.z, edge: min.x, sign: -1 },
      { axis: 'z', start: min.z, end: max.z, edge: max.x, sign: 1 },
    ];
  }

  /**
   * Catalog height facts for a whole collection, cached. `hasSeatHeights` is true when the API
   * publishes a productHeight for at least one of the line's modules. `height` is that value when
   * the whole line agrees on one number — the API lists productHeight on only a few SKUs per
   * collection but it is one number per product line, so a module with no entry of its own can
   * inherit it. A collection listing several distinct heights (e.g. `ta`) is ambiguous and gets
   * null, falling back to geometry.
   */
  getCollectionHeightInfo(collectionId) {
    if (!collectionId) return NO_COLLECTION_HEIGHT;
    const cached = this._collectionHeightCache.get(collectionId);
    if (cached) return cached;

    const collection = this.app.getFurnitureManager?.()?.getFurnitureCollections?.()?.[collectionId];
    // Nothing to read yet (catalog or API still loading) — answer without caching the blank.
    if (!this.productDimensions || !collection?.items) return NO_COLLECTION_HEIGHT;

    let distinct = null;
    let ambiguous = false;
    let hasSeatHeights = false;
    for (const item of collection.items) {
      if (item.componentType !== 'module') continue;
      const h = parseFloat(this.productDimensions[item.urlCode]?.productHeight);
      if (isNaN(h)) continue;
      hasSeatHeights = true;
      if (distinct === null) distinct = h;
      else if (distinct !== h) { ambiguous = true; break; }
    }

    const info = { height: ambiguous ? null : distinct, hasSeatHeights };
    this._collectionHeightCache.set(collectionId, info);
    return info;
  }

  // Does this element set the seat height? Tables, storage and backrest rails ship as
  // componentType `module` (bonbon3-d, bard-r1/r2, oiko-ts) but aren't seats, and the API marks
  // them by listing a length and width with NO productHeight while the line's real seat modules
  // all carry one. Collections the API gives no heights for at all (diagon, choice) are measured
  // from geometry, so nothing is excluded there — there's no signal to read.
  isSeatHeightModule(element) {
    const data = element.furnitureData;
    if (data?.componentType !== 'module') return false;
    const dims = this.productDimensions?.[data.urlCode];
    if (!dims || dims.productHeight != null) return true;
    return !this.getCollectionHeightInfo(data.collection).hasSeatHeights;
  }

  // The modules whose geometry defines this group's height: its seats, or — for a group holding no
  // seat at all (a lone bard-r1 rail, an oiko-ts table) — whatever modules it does have, measured
  // off the model since the catalog publishes no height for them.
  heightSourceModules(furniture) {
    const modules = [];
    const seats = [];
    for (const item of furniture) {
      if (item.furnitureData?.componentType !== 'module') continue;
      modules.push(item);
      if (this.isSeatHeightModule(item)) seats.push(item);
    }
    return seats.length > 0 ? { modules: seats, isSeats: true } : { modules, isSeats: false };
  }

  // Are this module's legs the user-selectable kind? Where they aren't (oiko, bonbon3, zip) the
  // model's own feet are simply part of the piece.
  hasAdjustableLegs(element) {
    const collections = this.app.getFurnitureManager?.()?.getFurnitureCollections?.() || {};
    return collections[element.furnitureData?.collection]?.hasLegs === true;
  }

  groupHasLegs(modules) {
    return modules.some(item => this.hasAdjustableLegs(item));
  }

  // Height of the module's legs, counted only in its lower region so top wood surfaces (oiko-ts)
  // don't register as legs. 0 when it has none.
  measureLegHeight(boxes, moduleBox) {
    const legBox = new THREE.Box3();
    const legCeiling = moduleBox.min.y + (moduleBox.max.y - moduleBox.min.y) * LEG_REGION_FRACTION;
    for (const { name, box: childBox } of boxes) {
      if (childBox.max.y <= legCeiling && LEG_PATTERNS.some(p => name.includes(p))) legBox.union(childBox);
    }
    return legBox.isEmpty() ? 0 : legBox.max.y - legBox.min.y;
  }

  // Bucket a module's measured height shares scene-wide. Seats pool per collection (one line, one
  // height); a non-seat module gets a bucket per SKU, since a collection's rails and tables have
  // no reason to agree.
  heightKey(element, isSeat) {
    const collection = element.furnitureData?.collection || '';
    return isSeat ? collection : `${collection}|${element.furnitureData?.urlCode || ''}`;
  }

  // Tallest seat-module catalog body height (productHeight, cm) in the group, or null. The
  // vertical-dimension base, so the user-selected leg height can be added on top.
  getApiBodyHeight(furniture) {
    let apiHeight = null;
    furniture.forEach(item => {
      if (!this.isSeatHeightModule(item)) return;
      const own = parseFloat(this.productDimensions?.[item.furnitureData?.urlCode]?.productHeight);
      const h = isNaN(own) ? this.getCollectionHeightInfo(item.furnitureData?.collection).height : own;
      if (h != null && (apiHeight === null || h > apiHeight)) apiHeight = h;
    });
    return apiHeight;
  }

  // Bounding box of the body that sets this group's height, from `heightSourceModules` (no
  // attachments). Returns { body: Box3, bodyByKey }, bodyByKey mapping a `heightKey` bucket to the
  // tallest measured body height (metres, legs excluded) in it.
  calculateSeaterBodyBoundingBox({ modules, isSeats }, frame = null) {
    const box = new THREE.Box3();
    const bodyByKey = new Map();
    let hasModules = false;

    modules.forEach(item => {
      const boxes = this.collectElementMeshBoxes(item, frame);
      if (boxes.length === 0) return;

      const moduleBox = new THREE.Box3();
      for (const b of boxes) moduleBox.union(b.box);
      box.union(moduleBox);
      hasModules = true;

      // Only adjustable legs come off: they're scaled to the selected height at runtime, so we
      // subtract what's on screen and the renderer adds the user's choice back on. Fixed feet
      // (oiko-ts's wood legs) are part of the piece and nothing would put them back.
      const bodyHeight = (moduleBox.max.y - moduleBox.min.y) - (this.hasAdjustableLegs(item) ? this.measureLegHeight(boxes, moduleBox) : 0);
      const key = this.heightKey(item, isSeats);
      const known = bodyByKey.get(key);
      if (known === undefined || bodyHeight > known) bodyByKey.set(key, bodyHeight);
    });

    if (!hasModules) return null;

    return { body: box, bodyByKey };
  }

  /* =====================
     Rendering
     ===================== */

  createDimensionLine() {
    const group = new THREE.Group();

    // Main line (lying flat on the floor, extends along X)
    const lineGeo = new THREE.PlaneGeometry(1, 0.006);
    lineGeo.rotateX(-Math.PI / 2);
    const lineMat = new THREE.MeshBasicMaterial({
      color: DIMENSION_COLOR,
      transparent: true,
      opacity: 0.9,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    const lineMesh = new THREE.Mesh(lineGeo, lineMat);
    group.add(lineMesh);
    group.userData.line = lineMesh;

    // Arrow head geometry (lying flat, pointing along +X)
    const arrowSize = 0.04;
    const arrowGeo = new THREE.BufferGeometry();
    const vertices = new Float32Array([
      arrowSize, 0, 0,
      0, 0, arrowSize / 2,
      0, 0, -arrowSize / 2
    ]);
    arrowGeo.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    const arrowMat = new THREE.MeshBasicMaterial({
      color: DIMENSION_COLOR,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide
    });

    // End arrow (+X direction)
    const endArrow = new THREE.Mesh(arrowGeo, arrowMat);
    group.add(endArrow);
    group.userData.endArrow = endArrow;

    // Start arrow (-X direction, flipped)
    const startArrow = new THREE.Mesh(arrowGeo.clone(), arrowMat);
    startArrow.rotation.y = Math.PI;
    group.add(startArrow);
    group.userData.startArrow = startArrow;

    group.renderOrder = 15;
    group.visible = false;
    return group;
  }

  /** One group's worth of meshes, created on demand and reused. */
  getSlot(index) {
    while (this.slots.length <= index) this.slots.push(this.createSlot());
    return this.slots[index];
  }

  createDimensionLabel() {
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.FrontSide
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 20;
    mesh.visible = false;
    return mesh;
  }

  // A slot holds one line + label per outline segment. A rectangle needs four, an L six, and a
  // group can change shape under the cursor — so they're grown on demand and never released.
  ensureSides(slot, count) {
    while (slot.lines.length < count) {
      const lineGroup = this.createDimensionLine();
      this.dimensionGroup.add(lineGroup);
      slot.lines.push(lineGroup);

      const label = this.createDimensionLabel();
      this.dimensionGroup.add(label);
      slot.texts.push(label);
    }
  }

  createSlot() {
    const lines = [];
    const texts = [];

    // Height line (vertical) with arrows
    const heightLineGroup = new THREE.Group();
    heightLineGroup.name = 'HeightLineGroup';

    // Main line
    const lineGeo = new THREE.PlaneGeometry(0.008, 1);
    const lineMat = new THREE.MeshBasicMaterial({
      color: DIMENSION_COLOR,
      transparent: true,
      opacity: 0.9,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide
    });

    const lineMesh = new THREE.Mesh(lineGeo, lineMat);
    heightLineGroup.add(lineMesh);

    // Arrow heads (triangles)
    const arrowSize = 0.04;
    const arrowGeo = new THREE.BufferGeometry();
    const arrowVertices = new Float32Array([
      0, arrowSize, 0,
      -arrowSize / 2, 0, 0,
      arrowSize / 2, 0, 0
    ]);
    arrowGeo.setAttribute('position', new THREE.BufferAttribute(arrowVertices, 3));

    const arrowMat = new THREE.MeshBasicMaterial({
      color: DIMENSION_COLOR,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide
    });

    // Top arrow
    const topArrow = new THREE.Mesh(arrowGeo, arrowMat);
    topArrow.position.y = 0.5;
    heightLineGroup.add(topArrow);

    // Bottom arrow (flipped)
    const bottomArrow = new THREE.Mesh(arrowGeo.clone(), arrowMat);
    bottomArrow.position.y = -0.5;
    bottomArrow.rotation.z = Math.PI;
    heightLineGroup.add(bottomArrow);

    heightLineGroup.renderOrder = 15;
    heightLineGroup.visible = false;
    this.dimensionGroup.add(heightLineGroup);

    // Height text meshes (two planes facing opposite directions)
    const heightTextGeo = new THREE.PlaneGeometry(1, 1);
    const heightTextMat = new THREE.MeshBasicMaterial({
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.FrontSide
    });

    // Front-facing text (readable from front)
    const heightText = new THREE.Mesh(heightTextGeo, heightTextMat);
    heightText.renderOrder = 20;
    heightText.visible = false;
    this.dimensionGroup.add(heightText);

    // Back-facing text (readable from back)
    const heightTextMatBack = new THREE.MeshBasicMaterial({
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.FrontSide
    });
    const heightTextBack = new THREE.Mesh(heightTextGeo.clone(), heightTextMatBack);
    heightTextBack.renderOrder = 20;
    heightTextBack.visible = false;
    this.dimensionGroup.add(heightTextBack);

    return { lines, texts, heightLine: heightLineGroup, heightText, heightTextBack };
  }

  getTextTexture(text) {
    const dark = this.isWhiteRoom();
    const cacheKey = `${text}_${dark ? 'dark' : 'light'}`;

    if (this.textCache.has(cacheKey)) {
      return this.textCache.get(cacheKey);
    }

    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = dark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)';
    ctx.lineWidth = 4;
    ctx.fillStyle = dark ? '#000000' : '#ffffff';
    ctx.font = 'Bold 58px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 4;

    this.textCache.set(cacheKey, texture);
    return texture;
  }

  // Lays a group out in its measuring frame, then turns every position and rotation back into
  // world space by +yaw (a no-op at yaw 0).
  render3DDimensions(slot, measurement) {
    const { bbox, seaterBbox, bodyHeightCm, yaw, hasLegs } = measurement;
    const min = bbox.min;
    const max = bbox.max;
    const is3D = this.is3DMode();
    const segments = measurement.segments || this.rectangleSegments(bbox);

    this.ensureSides(slot, segments.length);

    segments.forEach((seg, i) => {
      // `nx`/`nz` is the edge's outward normal — the line and its label sit that way off the
      // footprint, so an L's inner lines hang in the notch rather than over the piece.
      const alongX = seg.axis === 'x';
      const nx = alongX ? 0 : seg.sign;
      const nz = alongX ? seg.sign : 0;
      const mid = (seg.start + seg.end) / 2;
      const x = alongX ? mid : seg.edge;
      const z = alongX ? seg.edge : mid;
      const len = seg.end - seg.start;

      const lineP = this.frameToWorld(x + nx * LINE_OFFSET, z + nz * LINE_OFFSET, yaw);
      const g = slot.lines[i];
      g.visible = true;
      g.position.set(lineP.x, FLOOR_HEIGHT, lineP.z);
      g.rotation.y = (alongX ? 0 : QUARTER_TURN) + yaw;
      // The span comes straight from the true geometry, so the printed number always matches it.
      g.userData.line.scale.x = len;
      g.userData.startArrow.position.x = -len / 2;
      g.userData.endArrow.position.x = len / 2;

      const labelP = this.frameToWorld(x + nx * LABEL_OFFSET, z + nz * LABEL_OFFSET, yaw);
      const mesh = slot.texts[i];
      mesh.visible = true;
      mesh.position.set(labelP.x, FLOOR_HEIGHT, labelP.z);
      // The plane lies flat (rotation.x = -90°), so its local z spins it about world Y.
      mesh.rotation.z = LABEL_ROTATION[`${seg.axis}${seg.sign < 0 ? '-' : '+'}`][is3D ? 1 : 0] + yaw;
      mesh.scale.set(TEXT_SIZE * 4.5, TEXT_SIZE * 1.1, 1);
      mesh.material.map = this.getTextTexture(this.formatLength(len));
      mesh.material.needsUpdate = true;
    });

    for (let i = segments.length; i < slot.lines.length; i++) {
      slot.lines[i].visible = false;
      slot.texts[i].visible = false;
    }

    // Seater height dimension (vertical line at the bottom-right corner)
    if (seaterBbox && is3D && bodyHeightCm != null) {
      // The measured body excludes legs, so the user-selected height goes back on top.
      const legHeightCm = hasLegs ? parseFloat(this.app.getFurnitureManager?.()?.currentLegHeight || '8') : 0;
      const totalHeight = (bodyHeightCm + legHeightCm) / 100;
      const centerY = totalHeight / 2;

      // Hang it off the near-left edge of the DEEPEST part of the piece (the last -X outline
      // segment), not the bbox corner — on an L that corner is the empty notch.
      let anchorX = min.x;
      for (const seg of segments) if (seg.axis === 'z' && seg.sign < 0) anchorX = seg.edge;

      const lineP = this.frameToWorld(anchorX - HEIGHT_LINE_OFFSET, max.z + HEIGHT_LINE_OFFSET, yaw);
      slot.heightLine.visible = true;
      slot.heightLine.position.set(lineP.x, centerY, lineP.z);
      slot.heightLine.scale.set(1, totalHeight, 1);
      slot.heightLine.rotation.set(0, QUARTER_TURN + yaw, 0);

      // Two planes back to back, so the label reads from either side.
      const label = `${this.convertToUnit(totalHeight)} ${this.getUnitLabel()}`;
      const textP = this.frameToWorld(anchorX - HEIGHT_LINE_OFFSET, max.z + HEIGHT_LINE_OFFSET + 0.05, yaw);
      for (const [mesh, facing] of [[slot.heightText, -1], [slot.heightTextBack, 1]]) {
        mesh.visible = true;
        mesh.position.set(textP.x, centerY, textP.z);
        mesh.scale.set(TEXT_SIZE * 4.5, TEXT_SIZE * 1.1, 1);
        mesh.rotation.set(0, facing * QUARTER_TURN + yaw, -facing * QUARTER_TURN);
        mesh.material.map = this.getTextTexture(label);
        mesh.material.needsUpdate = true;
      }
    } else {
      // 2D, or nothing in the group with a known height.
      slot.heightLine.visible = false;
      slot.heightText.visible = false;
      slot.heightTextBack.visible = false;
    }
  }

  /* =====================
     Cleanup
     ===================== */

  dispose() {
    this.clear();
    this.textCache.forEach(tex => tex.dispose());
    this.textCache.clear();

    const scene = this.app.getScene();
    if (scene) scene.remove(this.dimensionGroup);
  }
}
